import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FirmSummaryDto, FirmResponseDto } from '../models/firm.dto';
import { FirmTier } from '../enums/firm-tier.enum';
import { FirmService } from './firm.service';
import { FirmContextService } from './firm-context.service';
import { UserService } from 'src/app/core/services/user.service';

/**
 * Single source of truth for the user's workspaces and which one is active.
 *
 * Responsibilities:
 *   - Load the user's firms once (no other code should call getMyFirms()).
 *   - Decide which workspace to activate on login (defaults to the Personal Workspace).
 *   - Auto-create the Individual-tier Personal Workspace so clients always work.
 *   - Expose reactive lists/getters that components read from.
 *
 * It delegates the low-level "active firm" state and X-Firm-Id header to FirmContextService.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceService {
  private initialized = false;
  private creatingPersonal = false;

  private readonly organizationsSubject = new BehaviorSubject<FirmSummaryDto[]>([]);
  private readonly personalSubject = new BehaviorSubject<FirmSummaryDto | null>(null);

  /** Organisation/Team workspaces the user belongs to (excludes the Personal Workspace). */
  readonly organizations$ = this.organizationsSubject.asObservable();
  /** The user's Individual-tier Personal Workspace, once known. */
  readonly personalWorkspace$ = this.personalSubject.asObservable();

  get organizations(): FirmSummaryDto[] { return this.organizationsSubject.value; }
  get personalWorkspace(): FirmSummaryDto | null { return this.personalSubject.value; }

  get organizationWorkspace(): FirmSummaryDto | undefined { return this.organizations[0]; }
  get hasOrganization(): boolean { return this.organizations.length > 0; }

  // ── Backwards-compatible aliases (existing templates/components read these) ──
  get firms(): FirmSummaryDto[] { return this.organizations; }
  readonly firms$ = this.organizations$;
  get organizationFirm(): FirmSummaryDto | undefined { return this.organizationWorkspace; }
  get personalFirm(): FirmSummaryDto | null { return this.personalWorkspace; }

  constructor(
    private readonly firmService: FirmService,
    private readonly firmContext: FirmContextService,
    private readonly userService: UserService,
  ) {}

  /** Load firms and activate a workspace. Safe to call repeatedly — runs once. */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.reload(true);
  }

  switchTo(firmId: string): void { this.activate(firmId); }

  switchToOrganizationFirm(): void {
    const org = this.organizationWorkspace;
    if (org) this.activate(org.id);
  }

  /** Activate the Personal Workspace, creating it if the user doesn't have one yet. */
  switchToPersonal(): void {
    const personal = this.personalSubject.value;
    if (personal) this.activate(personal.id);
    else this.createPersonalWorkspace(true);
  }

  /** Re-fetch the firm lists without changing the active workspace. */
  refreshFirms(): void { this.reload(false); }

  // ── internals ──────────────────────────────────────────────────────────────

  private reload(activate: boolean): void {
    this.firmService.getMyFirms().subscribe({
      next: (firms) => {
        const orgs = firms.filter(f => f.tier === FirmTier.Organization || f.tier === FirmTier.Team);
        const personal = firms.find(f => f.tier === FirmTier.Individual) ?? null;

        this.organizationsSubject.next(orgs);
        this.personalSubject.next(personal);

        if (!activate) return;

        // Login default = Personal Workspace (personal Free/Ultimate applies). An explicit
        // switch into an organisation is persisted and restored here so refreshes keep context.
        const savedId = this.firmContext.activeFirmId;
        const savedFirm = firms.find(f => f.id === savedId);

        if (savedFirm) this.activate(savedFirm.id);
        else if (personal) this.activate(personal.id);
        else this.createPersonalWorkspace(true);
      },
      error: () => this.firmContext.clearActiveFirm(),
    });
  }

  private createPersonalWorkspace(activate: boolean): void {
    if (this.creatingPersonal) return;
    this.creatingPersonal = true;

    this.userService.getCurrentUser().subscribe({
      next: (user) => {
        const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        this.firmService.createFirm({
          name: fullName || 'Personal Workspace',
          tier: FirmTier.Individual,
          primaryEmail: user.email,
        }).subscribe({
          next: (firm) => {
            this.creatingPersonal = false;
            this.personalSubject.next(this.toSummary(firm));
            if (activate) this.firmContext.setActiveFirm(firm);
          },
          error: () => { this.creatingPersonal = false; this.firmContext.clearActiveFirm(); },
        });
      },
      error: () => { this.creatingPersonal = false; this.firmContext.clearActiveFirm(); },
    });
  }

  private toSummary(firm: FirmResponseDto): FirmSummaryDto {
    return { id: firm.id, name: firm.name, tier: firm.tier, myRole: firm.myRole, isActive: firm.isActive };
  }

  private activate(firmId: string): void {
    this.firmService.switchActiveFirm(firmId).subscribe({
      next: (firm) => this.firmContext.setActiveFirm(firm),
      error: () => this.firmContext.clearActiveFirm(),
    });
  }
}
